// Pure decision helpers for admin/support impersonation + coach manage mode.
// Kept dependency-free (no Angular / PouchDB imports) so the logic is unit
// testable under plain node. See impersonation-helpers.test.ts.

/** The subset of CoachManagingClient this decision needs. Declared structurally
 *  so this file does not import the PouchDB-heavy user-db.service. */
export interface ManagingClientRef {
  clientUid: string;
}

/** Discriminated union so `clientUid` is guaranteed present exactly when
 *  `useSnapshot` is true. useSnapshot=true → load the managed client's DB as a
 *  read-only local snapshot (no live CouchDB sync), because the logged-in user
 *  is an admin impersonating a coach and is NOT a member of the client's DB (a
 *  live sync would 403 and boot fatally — #4192). false → keep today's behavior
 *  (live manage mode for a real coach; normal impersonation otherwise). */
export type ImpersonatedClientViewDecision =
  | { useSnapshot: true; clientUid: string }
  | { useSnapshot: false };

/**
 * Decide how to open a coach's client while an admin/support account is
 * impersonating that coach (#4192).
 *
 * A real coach is a CouchDB member of their client's DB, so they get the normal
 * live-sync manage mode. But an admin impersonating a DIFFERENT coach is
 * authenticated to CouchDB as the admin, who is not a member of that coach's
 * client DBs — a live sync returns 403 and the app boots to "App failed to
 * start". So when (and only when) a managing client is set AND an impersonation
 * session is active, we load the client's DB the same read-only way
 * impersonation already loads the coach's own DB.
 */
export function decideImpersonatedClientView(
  managingClient: ManagingClientRef | null | undefined,
  isImpersonating: boolean,
): ImpersonatedClientViewDecision {
  if (managingClient && isImpersonating) {
    return { useSnapshot: true, clientUid: managingClient.clientUid };
  }
  return { useSnapshot: false };
}
